import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { RefreshCcw } from 'lucide-react';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Input,
  toast,
} from '@helios/ui';
import { useAuth } from '@/features/auth/authContext';
import { LoadingRow, selectClassName } from '@/features/settings/settingsPanels';
import {
  SettingsPageFrame,
  SettingsField as Field,
  SettingsSection,
  SettingsTableScroll,
  settingsFilterRowClass,
} from '@/features/settings/settingsChrome';
import { useUserPreferences } from '@/features/settings/UserPreferencesProvider';
import { useAdminSessions, useAdminTenants, useAdminUserMutations } from '@/hooks/useAdmin';
import type { AdminSessionItem } from '@/services/adminClient';
import { getSessionOriginKey } from '@/services/sessionOrigin';

const validId = (value: string) =>
  value === '' ||
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

export function SettingsActivityPage() {
  const auth = useAuth();
  const scope = JSON.stringify([
    auth.user?.id,
    auth.tenant?.id,
    [...new Set(auth.permissions)].sort(),
    auth.sessionId,
    auth.authVersion,
    auth.accessMode,
    getSessionOriginKey(),
  ]);
  return <ActivityWorkspace key={scope} />;
}

function ActivityWorkspace() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const platform = hasPermission(auth.permissions, PERMISSIONS.PLATFORM_ADMIN);
  const tenantId = platform ? (params.get('tenantId') ?? '') : '';
  const userId = params.get('userId') ?? '';
  const q = params.get('q') ?? '';
  const pageText = Number(params.get('page') ?? 1);
  const page = Number.isInteger(pageText) && pageText > 0 ? pageText : 1;
  const validFilters = validId(userId) && validId(tenantId);
  const [userInput, setUserInput] = useState(userId);
  const [search, setSearch] = useState(q);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [tenantPage, setTenantPage] = useState(1);
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantQ, setTenantQ] = useState('');
  const [selectedTenantName, setSelectedTenantName] = useState('');
  const [target, setTarget] = useState<AdminSessionItem | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  useEffect(() => {
    setUserInput(userId);
    setSearch(q);
    setFilterError(null);
    setTarget(null);
    setActionError(null);
  }, [userId, tenantId, q]);
  const tenants = useAdminTenants(
    { page: tenantPage, pageSize: 25, q: tenantQ || undefined },
    platform,
  );
  const sessions = useAdminSessions(
    {
      page,
      pageSize: 25,
      tenantId: tenantId || undefined,
      userId: userId || undefined,
      q: q || undefined,
    },
    validFilters,
  );
  const { revokeSessions } = useAdminUserMutations();
  const { formatDateTime } = useUserPreferences();
  const items = sessions.data?.items ?? [];
  const meta = sessions.data?.meta;
  const tenantItems = tenants.data?.items ?? [];
  const currentTarget = target ? items.find((item) => item.userId === target.userId) : undefined;
  const targetChanged = Boolean(
    currentTarget &&
      target &&
      (currentTarget.userVersion !== target.userVersion || !currentTarget.canRevokeSessions),
  );
  const changeFilters = (values: Record<string, string>) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(values)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      next.delete('page');
      return next;
    });
  };
  const changePage = (nextPage: number) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (nextPage > 1) next.set('page', String(nextPage));
      else next.delete('page');
      return next;
    });
  const confirmRevocation = async () => {
    if (!target || busy || targetChanged || !target.canRevokeSessions) return;
    setBusy(true);
    setActionError(null);
    try {
      const result = await revokeSessions.mutateAsync({
        id: target.userId,
        input: { expectedVersion: target.userVersion },
      });
      if (!active.current) return;
      toast.success(
        `${result.sessionsRevoked} sign-ins and ${result.revokedTwinSessions} Twin sessions revoked`,
      );
      if (target.userId === auth.user?.id) {
        try {
          await auth.signOut();
        } finally {
          if (active.current) navigate('/auth', { replace: true });
        }
      } else {
        setTarget(null);
      }
    } catch (caught) {
      if (active.current)
        setActionError(caught instanceof Error ? caught.message : 'Revocation failed');
    } finally {
      if (active.current) setBusy(false);
    }
  };

  return (
    <SettingsPageFrame
      title="Activity"
      description="Sign-in history for authorized users. Revoked and expired sessions remain visible for investigation."
      actions={
        <Button type="button" variant="outline" size="xs" onClick={() => void sessions.refetch()}>
          <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Refresh
        </Button>
      }
    >
      {platform ? (
        <SettingsSection
          title="Tenant scope"
          description="Search and page the workspace directory before narrowing activity."
        >
          <form
            className={`${settingsFilterRowClass} sm:grid-cols-[1fr_auto]`}
            onSubmit={(event) => {
              event.preventDefault();
              setTenantQ(tenantSearch.trim());
              setTenantPage(1);
            }}
          >
            <Field label="Find tenant">
              <Input
                value={tenantSearch}
                onChange={(event) => setTenantSearch(event.target.value)}
                placeholder="Name or slug"
              />
            </Field>
            <Button type="submit" size="xs" variant="outline">
              Find tenants
            </Button>
          </form>
          <Field label="Tenant">
            <select
              className={selectClassName}
              value={tenantId}
              onChange={(event) => {
                setSelectedTenantName(
                  tenantItems.find((item) => item.id === event.target.value)?.name ?? '',
                );
                changeFilters({ tenantId: event.target.value, userId: '' });
              }}
            >
              <option value="">All tenants</option>
              {tenantId && !tenantItems.some((item) => item.id === tenantId) ? (
                <option value={tenantId}>{selectedTenantName || tenantId}</option>
              ) : null}
              {tenantItems.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </Field>
          {tenants.isError ? (
            <p role="alert" className="text-critical text-xs">
              Tenant directory unavailable. Refresh or search again.
            </p>
          ) : null}
          <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span>
              Tenant page {tenantPage} of {Math.max(1, tenants.data?.meta.totalPages ?? 1)}
            </span>
            <Button
              size="xs"
              variant="outline"
              disabled={tenantPage <= 1}
              onClick={() => setTenantPage((value) => value - 1)}
            >
              Previous tenants
            </Button>
            <Button
              size="xs"
              variant="outline"
              disabled={!tenants.data?.meta || tenantPage >= tenants.data.meta.totalPages}
              onClick={() => setTenantPage((value) => value + 1)}
            >
              Next tenants
            </Button>
          </div>
        </SettingsSection>
      ) : null}
      <SettingsSection
        title="Sessions"
        description="Revoke all sign-ins acts on the user, including other sessions and Twin credentials. It never targets only the displayed row."
      >
        <form
          className={`${settingsFilterRowClass} mb-2 md:grid-cols-[1fr_1fr_auto]`}
          onSubmit={(event) => {
            event.preventDefault();
            const next = userInput.trim();
            if (!validId(next)) {
              setFilterError('Enter a valid user UUID or leave User ID empty.');
              return;
            }
            setFilterError(null);
            changeFilters({ userId: next, q: search.trim() });
          }}
        >
          <Field label="User ID">
            <Input
              value={userInput}
              onChange={(event) => setUserInput(event.target.value)}
              placeholder="Optional user UUID"
            />
          </Field>
          <Field label="Search sessions">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="User name or email"
            />
          </Field>
          <div className="flex flex-wrap gap-1">
            <Button type="submit" size="xs" variant="outline">
              Apply activity filters
            </Button>
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={() => changeFilters({ userId: '', q: '', tenantId: '' })}
            >
              Clear filters
            </Button>
          </div>
        </form>
        {filterError || !validFilters ? (
          <p role="alert" className="text-critical mb-2 text-xs">
            {filterError ??
              'The URL contains an invalid user or tenant UUID. Clear filters to continue.'}
          </p>
        ) : null}
        {!validFilters ? null : sessions.isLoading ? (
          <LoadingRow label="Loading sessions…" />
        ) : sessions.isError ? (
          <ErrorState
            title="Activity unavailable"
            description="The admin activity API did not respond."
          />
        ) : items.length === 0 ? (
          <EmptyState title="No sessions" description="No sign-ins matched these filters." />
        ) : (
          <SettingsTableScroll label="Sign-in activity">
            <table className="w-full min-w-[980px] border-collapse text-left text-xs">
              <thead className="bg-card text-2xs text-muted-foreground uppercase tracking-wide">
                <tr>
                  {[
                    'User',
                    ...(platform ? ['Tenant'] : []),
                    'Started',
                    'Expires',
                    'Client',
                    'Status',
                    'Actions',
                  ].map((label) => (
                    <th key={label} className="border-border border-b px-3 py-2 font-medium">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((session) => (
                  <tr key={session.id} className="border-border/70 border-b last:border-b-0">
                    <td className="px-2 py-2">
                      <p className="text-foreground font-medium">{session.userName}</p>
                      <p className="text-2xs text-muted-foreground">{session.userEmail}</p>
                    </td>
                    {platform ? (
                      <td className="text-muted-foreground px-2 py-2">{session.tenantName}</td>
                    ) : null}
                    <td className="text-muted-foreground px-2 py-2">
                      {formatDateTime(session.createdAt)}
                    </td>
                    <td className="text-muted-foreground px-2 py-2">
                      {formatDateTime(session.expiresAt)}
                    </td>
                    <td className="text-muted-foreground px-2 py-2">
                      <p>{session.ipAddress ?? 'Unknown IP'}</p>
                      <p
                        className="text-2xs max-w-xs truncate"
                        title={session.userAgent ?? undefined}
                      >
                        {session.userAgent ?? 'Unknown agent'}
                      </p>
                    </td>
                    <td className="px-2 py-2">
                      <Badge variant={session.active ? 'success' : 'outline'}>
                        {session.active ? 'Active' : session.revokedAt ? 'Revoked' : 'Expired'}
                      </Badge>
                      {session.purpose === 'PASSWORD_CHANGE' ? (
                        <p className="text-2xs text-muted-foreground mt-1">Password change only</p>
                      ) : null}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap gap-1">
                        <Button
                          type="button"
                          size="xs"
                          variant="outline"
                          aria-label={`Filter user ${session.userName}`}
                          onClick={() =>
                            changeFilters({
                              userId: session.userId,
                              tenantId: platform ? session.tenantId : '',
                            })
                          }
                        >
                          Filter user
                        </Button>
                        <Button
                          type="button"
                          size="xs"
                          variant="outline"
                          aria-label={`Revoke all sign-ins for ${session.userName}`}
                          disabled={
                            !session.canRevokeSessions ||
                            !hasPermission(auth.permissions, PERMISSIONS.USERS_MANAGE)
                          }
                          onClick={() => {
                            setTarget({ ...session });
                            setActionError(null);
                          }}
                        >
                          Revoke all sign-ins
                        </Button>
                      </div>
                      {!session.canRevokeSessions ? (
                        <p className="text-2xs text-muted-foreground mt-1">
                          Your current access cannot revoke this account.
                        </p>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SettingsTableScroll>
        )}
        <div className="text-muted-foreground mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span>
            {meta && validFilters ? `${meta.total} sessions · ` : ''}Page {page} of{' '}
            {Math.max(1, meta?.totalPages ?? 1)}
          </span>
          <div className="flex gap-2">
            <Button
              size="xs"
              variant="outline"
              disabled={page <= 1 || !validFilters}
              onClick={() => changePage(page - 1)}
            >
              Previous sessions
            </Button>
            <Button
              size="xs"
              variant="outline"
              disabled={!meta || page >= meta.totalPages || !validFilters}
              onClick={() => changePage(page + 1)}
            >
              Next sessions
            </Button>
          </div>
        </div>
      </SettingsSection>
      {target ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open && !busy) setTarget(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Revoke all sign-ins</DialogTitle>
              <DialogDescription>
                Revoke every staff sign-in and Twin credential for {target.userName} (
                {target.userEmail}). They will need to sign in again.
                {target.userId === auth.user?.id
                  ? ' This is your account; you will be signed out.'
                  : ''}
              </DialogDescription>
            </DialogHeader>
            <p className="text-muted-foreground text-xs">
              Reviewed account version {target.userVersion}. No account status or role grants are
              changed.
            </p>
            {targetChanged ? (
              <p role="alert" className="text-warning text-xs">
                This account changed. Reload sessions and review its current version before
                confirming.
              </p>
            ) : null}
            {actionError ? (
              <p role="alert" className="text-critical text-xs">
                {actionError}
              </p>
            ) : null}
            <DialogFooter className="flex-wrap">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setActionError(null);
                  try {
                    const result = await sessions.refetch();
                    if (result.isError) throw result.error ?? new Error('Reload failed');
                    if (active.current) setTarget(null);
                  } catch (caught) {
                    if (active.current)
                      setActionError(caught instanceof Error ? caught.message : 'Reload failed');
                  } finally {
                    if (active.current) setBusy(false);
                  }
                }}
              >
                Reload sessions
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setTarget(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={busy || targetChanged || !target.canRevokeSessions}
                onClick={() => void confirmRevocation()}
              >
                {busy ? 'Revoking…' : 'Confirm revoke all sign-ins'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </SettingsPageFrame>
  );
}
